import axios from 'axios';
import type { PillTone } from '@bcecloud/portal-ui';
import { currentStaffToken } from './api';

/**
 * The contracts slice of the V3 API: e-signature contracts generated or
 * uploaded per event, edited via the provider's embedded builder, optionally
 * negotiated through redline rounds, then sent for signature.
 */

/** Contract lifecycle states (mirrors the API's CONTRACT_STATUS). */
export type ContractStatus =
  | 'draft'
  | 'client_review'
  | 'changes_requested'
  | 'approved'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'declined'
  | 'voided';

/** Status → pill tone, so state reads at a glance. */
export const CONTRACT_STATUS_TONES: Readonly<Record<ContractStatus, PillTone>> = {
  draft: 'neutral',
  client_review: 'info',
  changes_requested: 'warning',
  approved: 'brand',
  sent: 'info',
  viewed: 'info',
  signed: 'success',
  declined: 'danger',
  voided: 'neutral',
};

/** Status → human label. */
export const CONTRACT_STATUS_LABELS: Readonly<Record<ContractStatus, string>> = {
  draft: 'Draft',
  client_review: 'With client for review',
  changes_requested: 'Changes requested',
  approved: 'Client approved',
  sent: 'Out for signature',
  viewed: 'Viewed by signer',
  signed: 'Signed',
  declined: 'Declined',
  voided: 'Voided',
};

/** One entry on the redline/negotiation thread. */
export interface NegotiationEntry {
  readonly author: 'bce' | 'client';
  readonly authorName?: string;
  readonly message: string;
  readonly createdAt: string;
}

/** One signer on the signature round. */
export interface ContractSubmitter {
  readonly role: string;
  readonly name?: string;
  readonly email?: string;
  readonly slug?: string;
  readonly embedSrc?: string;
  readonly status?: string;
  readonly openedAt?: string;
  readonly completedAt?: string;
}

/** A contract as the API returns it. */
/** One imported client redline awaiting review. */
export interface RedlineProposal {
  readonly _id: string;
  readonly kind: 'replace' | 'insert' | 'delete';
  readonly originalText: string;
  readonly proposedText: string;
  readonly contextBefore: string;
  readonly contextAfter: string;
  readonly author: string;
  readonly status: 'proposed' | 'accepted' | 'rejected' | 'applied' | 'unmatched';
  readonly sourceName: string;
  readonly importedAt?: string;
}

/** What a contract is about: an event, or a campaign's agreement. */
/**
 * What a contract is about: an event, a campaign, or a client's package
 * request (§46) — the last carries its organization so the pages can find
 * their way back to the Organization page's Agreement tab.
 */
export type ContractSubject =
  | { readonly kind: 'event' | 'campaign'; readonly id: string }
  | { readonly kind: 'agreement'; readonly id: string; readonly organizationId: string };

/** The API's collection for a subject's contracts. */
function contractsBase(subject: ContractSubject): string {
  if (subject.kind === 'event') return `/api/v3/events/${subject.id}/contracts`;
  if (subject.kind === 'campaign') return `/api/v3/campaigns/${subject.id}/contracts`;
  return `/api/v3/agreement-requests/${subject.id}/contracts`;
}

/** Where the subject's contract pages live in the admin app. */
export function contractPaths(subject: ContractSubject): {
  readonly back: string;
  readonly page: (contractId: string, page: 'text' | 'preview' | 'countersign') => string;
} {
  if (subject.kind === 'agreement') {
    const base = `/organizations/${subject.organizationId}`;
    return {
      back: `${base}?tab=agreement`,
      page: (contractId, page) => `${base}/agreement-requests/${subject.id}/contracts/${contractId}/${page}`,
    };
  }
  const base = subject.kind === 'event' ? `/events/${subject.id}` : `/campaigns/${subject.id}`;
  return {
    back: subject.kind === 'event' ? `${base}?tab=contracts` : `${base}?tab=agreement`,
    page: (contractId, page) => `${base}/contracts/${contractId}/${page}`,
  };
}

/** The subject a route's params name: an event when `eventId` is set, else the campaign. */
export function subjectFromParams(params: {
  eventId?: string | undefined;
  campaignId?: string | undefined;
  requestId?: string | undefined;
  organizationId?: string | undefined;
}): ContractSubject {
  if (params.eventId) return { kind: 'event', id: params.eventId };
  if (params.requestId) return { kind: 'agreement', id: params.requestId, organizationId: params.organizationId ?? '' };
  return { kind: 'campaign', id: params.campaignId ?? '' };
}

export interface Contract {
  readonly _id: string;
  /** The event, when the contract is an event's; absent on a campaign's agreement. */
  readonly eventId?: string | null;
  /** The rest of a series the contract covers, beside `eventId`. */
  readonly additionalEventIds?: readonly string[];
  /** The campaign, when the contract is its agreement. */
  readonly campaignId?: string | null;
  readonly name: string;
  readonly contractNumber: string | null;
  readonly status: ContractStatus;
  readonly source: 'generated' | 'uploaded';
  readonly organizationId?: string | null;
  readonly provider: string;
  readonly revision: number;
  readonly negotiation: readonly NegotiationEntry[];
  readonly submitters: readonly ContractSubmitter[];
  readonly documents: ReadonlyArray<{ readonly name: string; readonly url: string }>;
  readonly sentAt?: string;
  readonly inviteLastSentAt?: string;
  readonly redlineProposals?: readonly RedlineProposal[];
  readonly redlineApproval?: {
    readonly status: 'pending' | 'approved' | 'declined' | 'edits-requested' | null;
    readonly approverEmail?: string;
    readonly requestedBy?: string;
    readonly requestedAt?: string;
    readonly decidedAt?: string;
    readonly reason?: string;
  };
  readonly signedAt?: string;
  readonly declinedAt?: string;
  readonly createdAt: string;
}

/** What the embedded builder needs: provider kind + short-lived token. */
export interface BuilderEmbed {
  readonly kind: string;
  readonly token: string;
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await currentStaffToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listContracts(
  subject: ContractSubject,
  signal?: AbortSignal,
): Promise<Contract[]> {
  const response = await axios.get<{ data: Contract[] }>(contractsBase(subject), {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** Options for library-based generation. */
export interface GenerateContractInput {
  readonly kind: 'standard' | 'virtual' | 'enterprise';
  readonly sections: readonly string[];
  readonly attachmentB: readonly string[];
  /** Optional yyyy-mm-dd; blank falls back to the event date server-side. */
  readonly priceValidUntil?: string;
  /** Sibling events this contract also covers (multi-event contracts). */
  readonly additionalEventIds?: readonly string[];
}

/** What the series lookup found (mirrors the API's CONTRACT_SERIES_RESULT). */
export const CONTRACT_SERIES_RESULT = {
  series: 'series',
  unavailable: 'unavailable',
} as const;

/** Why no series could be read (mirrors the API's CONTRACT_SERIES_UNAVAILABLE). */
export const CONTRACT_SERIES_UNAVAILABLE = {
  noOpportunity: 'no_opportunity',
  notFound: 'not_found',
  unreachable: 'unreachable',
} as const;
export type ContractSeriesUnavailableReason = (typeof CONTRACT_SERIES_UNAVAILABLE)[keyof typeof CONTRACT_SERIES_UNAVAILABLE];

/** One Opportunity in a Salesforce series, with its event when BCEcloud has one. */
export interface ContractSeriesMember {
  readonly opportunityId: string;
  readonly name: string | null;
  readonly contractNumber: string | null;
  readonly stageName: string | null;
  /** `YYYY-MM-DD`. */
  readonly eventDate: string | null;
  readonly isParent: boolean;
  readonly eventId: string | null;
  /** Attachment B items Salesforce picked for the program and headcount (BES-32). */
  readonly attachmentBItems: readonly string[];
}

/** The series a new contract should cover — one contract per series, as S-Docs does. */
export type ContractSeriesView =
  | {
      readonly result: typeof CONTRACT_SERIES_RESULT.series;
      /** Where the contract is saved: the parent's event, else the event asked about. */
      readonly primaryEventId: string;
      readonly members: readonly ContractSeriesMember[];
    }
  | { readonly result: typeof CONTRACT_SERIES_RESULT.unavailable; readonly reason: ContractSeriesUnavailableReason };

/**
 * Which events a new contract covers, and where it is saved, given the
 * series and the members someone unticked. The primary (the parent's event
 * when BCEcloud has it) is always covered; members BCEcloud has no event for
 * cannot be.
 *
 * @param series - The series lookup; anything but a series covers just this event.
 * @param currentEventId - The event the dialog was opened from.
 * @param excludedEventIds - Events unticked in the dialog.
 * @returns The event to generate on and the rest it covers.
 */
export function seriesCoverage(
  series: ContractSeriesView | undefined,
  currentEventId: string,
  excludedEventIds: ReadonlySet<string>,
): { readonly primaryEventId: string; readonly additionalEventIds: readonly string[] } {
  if (series?.result !== CONTRACT_SERIES_RESULT.series) {
    return { primaryEventId: currentEventId, additionalEventIds: [] };
  }
  const additionalEventIds = series.members
    .map((member) => member.eventId)
    .filter((id): id is string => id !== null && id !== series.primaryEventId && !excludedEventIds.has(id));
  return { primaryEventId: series.primaryEventId, additionalEventIds };
}

/**
 * The Attachment B items to pre-tick, as Salesforce picks them from each
 * covered opportunity's program and headcount: every item any covered event
 * carries, in the library's order, and only items the library knows.
 *
 * @param series - The series lookup.
 * @param coveredEventIds - The events the contract covers.
 * @param libraryItems - The clause library's Attachment B item names.
 * @returns The items to pre-tick; empty when there is no series.
 */
export function defaultAttachmentB(
  series: ContractSeriesView | undefined,
  coveredEventIds: readonly string[],
  libraryItems: readonly string[],
): string[] {
  if (series?.result !== CONTRACT_SERIES_RESULT.series) return [];
  const picked = new Set(
    series.members
      .filter((member) => member.eventId !== null && coveredEventIds.includes(member.eventId))
      .flatMap((member) => member.attachmentBItems),
  );
  return libraryItems.filter((item) => picked.has(item));
}

/** The Salesforce series an event belongs to, read live. */
export async function readContractSeries(eventId: string, signal?: AbortSignal): Promise<ContractSeriesView> {
  const response = await axios.get<{ data: ContractSeriesView }>(`/api/v3/events/${eventId}/contract-series`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

export async function generateContract(
  subject: ContractSubject,
  input?: GenerateContractInput,
): Promise<Contract> {
  const response = await axios.post<{ data: Contract }>(
    contractsBase(subject),
    input ?? {},
    { headers: await authHeader() },
  );
  return response.data.data;
}

/** The clause library shaped for the generation dialog. */
export interface ContractLibrary {
  readonly sections: readonly string[];
  readonly attachmentB: readonly string[];
  readonly defaults: {
    readonly standard: readonly string[];
    readonly virtual: readonly string[];
    readonly enterprise: readonly string[];
  };
  /** The canonical document stylesheet — the editor injects it, scoped. */
  readonly stylesheet: string;
  /** The first-page brand row (logo + address), rendered above the editor. */
  readonly brandHtml: string;
}

export async function readContractLibrary(signal?: AbortSignal): Promise<ContractLibrary> {
  const response = await axios.get<{ data: ContractLibrary }>('/api/v3/contracts/library', {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

export async function uploadContract(subject: ContractSubject, file: File): Promise<Contract> {
  const form = new FormData();
  form.append('file', file);
  const response = await axios.post<{ data: Contract }>(
    `${contractsBase(subject)}/upload`,
    form,
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function readContract(contractId: string, signal?: AbortSignal): Promise<Contract> {
  const response = await axios.get<{ data: Contract }>(`/api/v3/contracts/${contractId}`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** Where the invite sends the signer, and what the email calls that place. */
export interface SigningLink {
  readonly url: string;
  readonly portalName: string;
}

/**
 * The link a contract's invite carries: the client portal for a signer who is a
 * member, My Events otherwise. From the server, so the copied link matches the email.
 */
export async function readSigningLink(contractId: string): Promise<SigningLink> {
  const response = await axios.get<{ data: SigningLink }>(`/api/v3/contracts/${contractId}/signing-link`, {
    headers: await authHeader(),
  });
  return response.data.data;
}

export async function readContractPreviewPages(
  contractId: string,
  signal?: AbortSignal,
): Promise<{ url: string | null }> {
  const response = await axios.get<{ data: { url: string | null } }>(
    `/api/v3/contracts/${contractId}/preview-pages`,
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

export async function readContractDocument(
  contractId: string,
  signal?: AbortSignal,
): Promise<{ html: string }> {
  const response = await axios.get<{ data: { html: string } }>(
    `/api/v3/contracts/${contractId}/document`,
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

export async function refreshContractData(contractId: string): Promise<Contract> {
  const response = await axios.post<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/refresh-data`,
    {},
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function updateContractDocument(contractId: string, html: string): Promise<Contract> {
  const response = await axios.patch<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/document`,
    { html },
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function readBuilderEmbed(contractId: string): Promise<BuilderEmbed> {
  const response = await axios.post<{ data: BuilderEmbed }>(
    `/api/v3/contracts/${contractId}/builder-token`,
    {},
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function shareContractForReview(
  contractId: string,
  input: { organizationId?: string; message?: string },
): Promise<Contract> {
  const response = await axios.post<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/share-for-review`,
    input,
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function resendContractInvite(
  contractId: string,
  message?: string,
): Promise<Contract> {
  const response = await axios.post<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/resend-invite`,
    message !== undefined && message.trim() !== '' ? { message } : {},
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function publishContract(
  contractId: string,
  input: {
    organizationId?: string;
    signerName?: string;
    signerEmail?: string;
    emailSigner: boolean;
    inviteMessage?: string;
  },
): Promise<Contract> {
  const response = await axios.post<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/publish`,
    input,
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function importRedlines(contractId: string, file: File): Promise<Contract> {
  const form = new FormData();
  form.append('file', file);
  const response = await axios.post<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/redlines`,
    form,
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function describeRedlines(contractId: string, description: string): Promise<Contract> {
  const response = await axios.post<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/redlines`,
    { description },
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function reviewRedline(
  contractId: string,
  proposalId: string,
  status: 'accepted' | 'rejected',
): Promise<Contract> {
  const response = await axios.patch<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/redlines/${proposalId}`,
    { status },
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function applyRedlines(
  contractId: string,
): Promise<{ contract: Contract; applied: number; unmatched: number }> {
  const response = await axios.post<{ data: Contract; meta: { applied: number; unmatched: number } }>(
    `/api/v3/contracts/${contractId}/redlines/apply`,
    {},
    { headers: await authHeader() },
  );
  return {
    contract: response.data.data,
    applied: response.data.meta.applied,
    unmatched: response.data.meta.unmatched,
  };
}

export async function readRedlineApprovers(signal?: AbortSignal): Promise<string[]> {
  const response = await axios.get<{ data: { approvers: string[] } }>(
    '/api/v3/contracts/redline-approvers',
    { headers: await authHeader(), signal },
  );
  return response.data.data.approvers;
}

export async function updateRedlineApprovers(approvers: string[]): Promise<string[]> {
  const response = await axios.put<{ data: { approvers: string[] } }>(
    '/api/v3/contracts/redline-approvers',
    { approvers },
    { headers: await authHeader() },
  );
  return response.data.data.approvers;
}

export async function requestRedlineApproval(
  contractId: string,
  approverEmail: string,
): Promise<Contract> {
  const response = await axios.post<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/redlines/request-approval`,
    { approverEmail },
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function voidContract(contractId: string): Promise<Contract> {
  const response = await axios.post<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/void`,
    {},
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function replyToNegotiation(
  contractId: string,
  message: string,
): Promise<Contract> {
  const response = await axios.post<{ data: Contract }>(
    `/api/v3/contracts/${contractId}/negotiation`,
    { message },
    { headers: await authHeader() },
  );
  return response.data.data;
}

// ─── Stripe integration config (admin) ─────────────────────────────────

export interface StripeConfigStatus {
  readonly source: 'settings' | 'env' | null;
  readonly publishableKey: string | null;
  readonly secretKeyLast4: string | null;
}

export async function readStripeConfig(signal?: AbortSignal): Promise<StripeConfigStatus> {
  const response = await axios.get<{ data: StripeConfigStatus }>('/api/v3/payments/stripe-config', {
    headers: await authHeader(),
    signal,
  });
  return response.data.data;
}

export async function saveStripeConfig(input: {
  secretKey: string;
  publishableKey?: string;
  webhookSecret?: string;
}): Promise<StripeConfigStatus> {
  const response = await axios.put<{ data: StripeConfigStatus }>(
    '/api/v3/payments/stripe-config',
    input,
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function clearStripeConfig(): Promise<StripeConfigStatus> {
  const response = await axios.delete<{ data: StripeConfigStatus }>(
    '/api/v3/payments/stripe-config',
    { headers: await authHeader() },
  );
  return response.data.data;
}

/** The most the credit-card surcharge can be (Visa's cap); mirrors the API. */
export const CARD_SURCHARGE_MAX_PERCENT = 3;

/** GET /payments/card-surcharge — the credit-card surcharge percentage (0 = off). */
export async function readCardSurcharge(signal?: AbortSignal): Promise<{ percent: number }> {
  const response = await axios.get<{ data: { percent: number } }>('/api/v3/payments/card-surcharge', {
    headers: await authHeader(),
    signal,
  });
  return response.data.data;
}

/** PUT /payments/card-surcharge — set it; 0 turns it off. */
export async function saveCardSurcharge(percent: number): Promise<{ percent: number }> {
  const response = await axios.put<{ data: { percent: number } }>(
    '/api/v3/payments/card-surcharge',
    { percent },
    { headers: await authHeader() },
  );
  return response.data.data;
}

export interface DocusealConfigStatus {
  source: 'settings' | 'env' | null;
  apiKeyLast4: string | null;
  webhookSecretSet: boolean;
}

export async function readDocusealConfig(signal?: AbortSignal): Promise<DocusealConfigStatus> {
  const response = await axios.get<{ data: DocusealConfigStatus }>(
    '/api/v3/contracts/docuseal-config',
    { headers: await authHeader(), signal },
  );
  return response.data.data;
}

export async function saveDocusealConfig(input: {
  apiKey: string;
  webhookSecret?: string;
}): Promise<DocusealConfigStatus> {
  const response = await axios.put<{ data: DocusealConfigStatus }>(
    '/api/v3/contracts/docuseal-config',
    input,
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function clearDocusealConfig(): Promise<DocusealConfigStatus> {
  const response = await axios.delete<{ data: DocusealConfigStatus }>(
    '/api/v3/contracts/docuseal-config',
    { headers: await authHeader() },
  );
  return response.data.data;
}

export interface NotificationEmails {
  contractEmails: string[];
  paymentEmails: string[];
}

export async function readNotificationEmails(signal?: AbortSignal): Promise<NotificationEmails> {
  const response = await axios.get<{ data: NotificationEmails }>(
    '/api/v3/contracts/notification-emails',
    { headers: await authHeader(), signal },
  );
  return response.data.data;
}

export async function saveNotificationEmails(input: NotificationEmails): Promise<NotificationEmails> {
  const response = await axios.put<{ data: NotificationEmails }>(
    '/api/v3/contracts/notification-emails',
    input,
    { headers: await authHeader() },
  );
  return response.data.data;
}

/** Sales → Contracts' filter tabs (mirrors the API's CONTRACT_LIST_FILTER). */
export const CONTRACT_LIST_FILTER = {
  open: 'open',
  signed: 'signed',
  closed: 'closed',
  all: 'all',
} as const;
export type ContractListFilter = (typeof CONTRACT_LIST_FILTER)[keyof typeof CONTRACT_LIST_FILTER];

/** One row on Sales → Contracts: an event contract with its event's details. */
export interface SalesContractRow {
  readonly id: string;
  readonly name: string;
  readonly status: ContractStatus;
  readonly contractNumber: string | null;
  readonly eventId: string;
  /** How many events the contract covers: more than one for a series contract. */
  readonly eventCount: number;
  readonly eventName: string | null;
  readonly eventDate: string | null;
  readonly accountName: string | null;
  readonly opportunityId: string | null;
  readonly sentAt: string | null;
  readonly signedAt: string | null;
  readonly updatedAt: string | null;
}

/**
 * Every event contract in one filter tab, most recently touched first
 * (needs the manage_contracts grant).
 */
export async function listSalesContracts(
  filter: ContractListFilter,
  signal?: AbortSignal,
): Promise<SalesContractRow[]> {
  const response = await axios.get<{ data: SalesContractRow[] }>('/api/v3/contracts', {
    params: { filter },
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}
